import React, { useCallback, useState } from "react";
import { Lobby } from "./lobby/Lobby";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store/reducers";
import { Room } from "./room/Room";
import { useDetectMediaDeviceChange } from "../hooks/useDetectMediaDeviceChange";
import { setErrorNotification } from "store/errors/slice";
import { ErrorType } from "store/errors/types";
import { twilioSyncEnabled } from "utils";
import { useLogger } from "hooks/useLogger";
import { client } from "api/client";
import { SyncClient } from "twilio-sync";
import { useInterval } from "hooks/useInterval";

const PING_SYNC_INTERVAL = 30000; // 30 seconds

interface VideoChatProps {
  roomUid: string;
}

export const VideoChat: React.FC<VideoChatProps> = ({ roomUid }) => {
  useLogger();
  const dispatch = useDispatch();
  const localTracks = useSelector((s: RootState) => s.localTracks);
  const mediaDevices = useSelector((s: RootState) => s.mediaDevices);
  const user = useSelector((s: RootState) => s.user);
  const { knockingList, knockingUser } = useSelector((s: RootState) => s.sync);
  const [syncClient, setSyncClient] = useState<SyncClient>();
  useDetectMediaDeviceChange(mediaDevices, localTracks);

  useInterval(() => {
    if (knockingList && knockingUser) {
      knockingList.update(knockingUser.index, {
        ping: Date.now(),
        lastUpdatedBy: "self",
      });
    }
  }, PING_SYNC_INTERVAL);

  const fetchSyncClient = useCallback(
    async (roomCode: string) => {
      if (!twilioSyncEnabled) {
        return;
      }

      async function updateToken(syncClient: SyncClient) {
        const { Token } = await client.twilio_PostSyncGuestToken({
          Room: roomCode,
        });

        syncClient.updateToken(Token);
      }

      try {
        const { Token } = await client.twilio_PostSyncGuestToken({
          Room: roomCode,
        });

        const syncClient = new SyncClient(Token, {
          logLevel: process.env.REACT_APP_TWILIO_LOGLEVEL || "debug",
        });
        syncClient.on("tokenAboutToExpire", () => updateToken(syncClient));
        syncClient.on("tokenExpired", () => updateToken(syncClient));

        setSyncClient(syncClient);
      } catch (e) {
        dispatch(
          setErrorNotification({
            type: ErrorType.ConnectionError,
            name: "Connection Error",
            message:
              "There was a problem establishing the connection. Try again.",
          })
        );
      }
    },
    [dispatch]
  );

  return (
    <div>
      <main>
        {!user.twilioToken && (
          <Lobby
            roomUid={roomUid}
            syncClient={syncClient}
            fetchSyncClient={fetchSyncClient}
          />
        )}
        {user.twilioToken &&
          localTracks.audioTrackInfo &&
          localTracks.videoTrackInfo && (
            <Room roomUid={roomUid} syncClient={syncClient} />
          )}
      </main>
    </div>
  );
};
